// src/components/admin/villa/VillaManagement.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationCenter } from '../../../context/NotificationCenterContext';
import VillaTable from './VillaTable';
import VillaForm from './VillaForm';
import Modal from '../../common/Modal';
import Loading from '../../common/Loading';
import { villaService } from '../../../services/villa';
import { FaPlus, FaSearch, FaTimes, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';

const VillaManagement = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // ✅ TAMBAH: state untuk total items
  const [loading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    location: '',
    status: '',
    featured: '',
    property_status: ''
  });

  const dropdownDataLoaded = useRef(false);
  const isFirstMount = useRef(true);

  const memoizedFilters = useMemo(() => filters, [
    filters.search,
    filters.category,
    filters.location,
    filters.status,
    filters.featured,
    filters.property_status
  ]);

  const { addNotification: addToCenter } = useNotificationCenter();

  const handleApiError = useCallback(async (error, context = '') => {
    console.error(`Error ${context}:`, error);
    
    let errorMessage = '';
    if (error.message.includes('404')) {
      errorMessage = `API endpoint not found. Make sure backend server is running at http://localhost:5000`;
    } else if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
      errorMessage = `Cannot connect to server. Make sure backend server is running.`;
    } else {
      errorMessage = `An error occurred: ${error.message}`;
    }
    
    await Swal.fire({
      title: 'Error',
      text: errorMessage,
      icon: 'error',
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'OK',
      customClass: {
        popup: 'rounded-2xl',
        title: 'text-xl font-bold',
        confirmButton: 'px-6 py-3 rounded-xl font-semibold'
      }
    });
  }, []);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      
      queryParams.append('page', currentPage);
      queryParams.append('limit', 10);
      
      Object.entries(memoizedFilters).forEach(([key, value]) => {
        if (value && value !== '' && value !== 'undefined') {
          if (key === 'featured') {
            if (value === '1') {
              queryParams.append(key, 'true');
            } else if (value === '0') {
              queryParams.append(key, 'false');
            }
          } else {
            queryParams.append(key, value);
          }
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/properties?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      setProperties(data.properties || []);
      setTotalPages(data.totalPages || 1);
      // ✅ TAMBAH: set totalItems dari response backend
      // Sesuaikan key-nya dengan response backend kamu (totalItems / total / count)
      setTotalItems(data.totalItems || data.total || data.count || 0);
      
      if (isFirstMount.current && data.properties && data.properties.length > 0) {
        addToCenter({
          type: 'success',
          title: 'Properties Loaded',
          message: `Successfully loaded ${data.properties.length} properties`
        });
        isFirstMount.current = false;
      }
      
    } catch (error) {
      handleApiError(error, 'fetching properties');
      setProperties([]);
      setTotalPages(1);
      setTotalItems(0); // ✅ TAMBAH: reset totalItems saat error
    } finally {
      setLoading(false);
    }
  }, [currentPage, memoizedFilters, handleApiError, addToCenter]);

  const fetchDropdownData = useCallback(async () => {
    if (dropdownDataLoaded.current) return;

    dropdownDataLoaded.current = true;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dropdown/all`);
      
      if (!response.ok) {
        dropdownDataLoaded.current = false;
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        const { categories, locations, facilities } = result.data;
        
        setCategories(categories || []);
        setLocations(locations || []);
        setFacilities(facilities || []);
        
        isFirstMount.current = false;
      } else {
        dropdownDataLoaded.current = false;
        throw new Error('Response format not matched');
      }
      
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      dropdownDataLoaded.current = false;
      handleApiError(error, 'fetching dropdown data');
      
      setCategories([]);
      setLocations([]);
      setFacilities([]);
    }
  }, [handleApiError]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleCreate = useCallback(() => {
    setEditingProperty(null);
    setShowForm(true);
  }, []);

  const handleEdit = useCallback(async (villa) => {
    setLoadingEdit(true);
    try {
      let fullVillaData = villa;
      
      if (villa.id) {
        try {
          fullVillaData = await villaService.getById(villa.id);
          
          addToCenter({
            type: 'info',
            title: 'Editing Property',
            message: `Loading data for "${villa.title}"`
          });
        } catch (fetchError) {
          console.error('Error fetching full villa data:', fetchError);
          
          addToCenter({
            type: 'warning',
            title: 'Limited Data',
            message: 'Using limited data for editing'
          });
        }
      }
      
      setEditingProperty(fullVillaData);
      setShowForm(true);
      
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: 'Failed to load villa data for editing',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    } finally {
      setLoadingEdit(false);
    }
  }, [addToCenter]);

  const handleDelete = useCallback(async (id, propertyName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/properties/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      fetchProperties();
    } catch (error) {
      handleApiError(error, 'deleting property');
      throw error;
    }
  }, [fetchProperties, handleApiError]);

  const handleToggleFeatured = useCallback(async (id, currentStatus, propertyName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/properties/${id}/featured`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ featured: !currentStatus }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      fetchProperties();
    } catch (error) {
      handleApiError(error, 'toggling featured status');
      throw error;
    }
  }, [fetchProperties, handleApiError]);

  const handleStatusChange = useCallback(async (id, status, propertyName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/properties/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      fetchProperties();
    } catch (error) {
      handleApiError(error, 'updating status');
      throw error;
    }
  }, [fetchProperties, handleApiError]);

  const handleFormSuccess = useCallback(async () => {
    const isEditing = editingProperty !== null;
    const propertyName = editingProperty?.title || 'New Property';
    
    setShowForm(false);
    setEditingProperty(null);
    fetchProperties();

    await Swal.fire({
      title: 'Success!',
      html: isEditing 
        ? `<strong>"${propertyName}"</strong> has been successfully updated.`
        : '<strong>New property</strong> has been successfully created.',
      icon: 'success',
      confirmButtonColor: '#10b981',
      confirmButtonText: 'OK',
      timer: 3000,
      timerProgressBar: true,
      customClass: {
        popup: 'rounded-2xl',
        title: 'text-xl font-bold',
        confirmButton: 'px-6 py-3 rounded-xl font-semibold'
      }
    });
    
    addToCenter({
      type: 'success',
      title: isEditing ? 'Property Updated' : 'Property Created',
      message: isEditing 
        ? `"${propertyName}" has been successfully updated` 
        : 'New property has been successfully created'
    });
  }, [fetchProperties, editingProperty, addToCenter]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      search: '',
      category: '',
      location: '',
      status: '',
      featured: '',
      property_status: ''
    });
    setCurrentPage(1);
    
    addToCenter({
      type: 'info',
      title: 'Filters Reset',
      message: 'All filters have been cleared'
    });
  }, [addToCenter]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingProperty(null);
  }, []);

  const handleViewPublicPage = useCallback(() => {
    navigate('/villas');
  }, [navigate]);

  const isMasterDataLoaded = categories.length > 0 || locations.length > 0 || facilities.length > 0;
  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  if (loading && properties.length === 0) return <Loading />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-amber-50/30 to-gray-50 p-8">
      <div className="max-w-9xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Property Management</h1>
            <p className="text-gray-600">Manage your villa listings and properties</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleViewPublicPage}
              className="inline-flex items-center px-6 py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
            >
              <FaEye className="mr-2 h-4 w-4" />
              View Public Page
            </button>
            
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-6 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
            >
              <FaPlus className="mr-2 h-4 w-4" />
              Add New Property
            </button>
          </div>
        </div>

        {/* Connection Status Warning */}
        {!isMasterDataLoaded && !dropdownDataLoaded.current && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-amber-800">Backend Connection Warning</h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>Cannot connect to backend server. Please ensure:</p>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li>Backend server is running at http://localhost:5000</li>
                    <li>API endpoint /api/dropdown/all is available</li>
                    <li>CORS is configured correctly</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading Edit Indicator */}
        {loadingEdit && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center">
              <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-sm font-medium text-amber-700">Loading villa data for editing...</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-200/50 p-4">
          <div className="flex flex-wrap gap-2.5">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search properties..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all"
                />
              </div>
            </div>

            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="px-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all w-auto"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>

            <select
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className="px-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all w-auto"
            >
              <option value="">All Locations</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.area_name || location.name}
                </option>
              ))}
            </select>

            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all w-auto"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={filters.featured}
              onChange={(e) => handleFilterChange('featured', e.target.value)}
              className="px-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all w-auto"
            >
              <option value="">Featured</option>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>

            <select
              value={filters.property_status}
              onChange={(e) => handleFilterChange('property_status', e.target.value)}
              className="px-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all w-auto"
            >
              <option value="">Property Type</option>
              <option value="leasehold">Leasehold</option>
              <option value="freehold">Freehold</option>
            </select>

            {activeFiltersCount > 0 && (
              <button
                onClick={resetFilters}
                className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all"
              >
                <FaTimes className="mr-1.5 h-3 w-3" />
                Reset
              </button>
            )}
          </div>

          {activeFiltersCount > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs font-medium text-gray-600">{activeFiltersCount} active:</span>
                {filters.search && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    "{filters.search.substring(0, 25)}{filters.search.length > 25 ? '...' : ''}"
                    <button onClick={() => handleFilterChange('search', '')} className="ml-1.5 hover:text-amber-900">
                      <FaTimes className="h-2.5 w-2.5" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <VillaTable
          properties={properties}
          categories={categories}
          locations={locations}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleFeatured={handleToggleFeatured}
          onStatusChange={handleStatusChange}
          loading={loading}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems} // ✅ TAMBAH: kirim totalItems ke VillaTable
          onPageChange={handlePageChange}
        />

        {/* Form Modal */}
        <Modal
          isOpen={showForm}
          onClose={handleCloseForm}
          title={editingProperty ? `Edit Property: ${editingProperty.title || 'Loading...'}` : 'Add New Property'}
          size="xl"
        >
          {showForm && (
            <VillaForm
              villa={editingProperty}
              onSuccess={handleFormSuccess}
              onCancel={handleCloseForm}
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default VillaManagement;